import BaseService from './base.service';
import { InviteStatus } from '../utils/constants/invite-status';

export default class OrgPropertiesService {
    static ENDPOINT = '/orgs';

    static async getOrgProperties(orgId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties`;
        return BaseService.get(endpoint);
    }

    static async getProperty(orgId, id) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${id}`;
        return BaseService.get(endpoint);
    }

    static async getPendingInvitesFromOrg(orgId) {
        return BaseService.get(`${this.ENDPOINT}/${orgId}/properties/pending-invites`, {
            org_id: orgId,
            status: [InviteStatus.PENDING_APPROVAL]
        });
    }

    static async addProperty(orgId, propertyData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties`;
        return BaseService.post(endpoint, propertyData);
    }

    static async batchImportProperties(orgId, propertiesData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/batch`;
        return BaseService.post(endpoint, propertiesData);
    }

    static async editProperty(orgId, propertyId, propertyData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}`;
        return BaseService.patch(endpoint, propertyData);
    }

    static async addPropertyNote(orgId, propertyId, noteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/notes`;
        return BaseService.post(endpoint, noteData);
    }

    static async editPropertyNote(orgId, propertyId, noteId, noteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/notes/${noteId}`;
        return BaseService.patch(endpoint, noteData);
    }

    static async removePropertyNote(orgId, propertyId, noteId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/notes/${noteId}`;
        return BaseService.delete(endpoint);
    }

    static async clearPropertyData(orgId, propertyId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/clear-data`;
        return BaseService.post(endpoint, null);
    }

    static async updatePropertyVehicleLicenseCounts(
        orgId,
        propertyId,
        vehicleLicenseCountsData
    ) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/vehicle-license-counts`;
        return BaseService.patch(endpoint, vehicleLicenseCountsData);
    }

    static async deleteProperty(orgId, propertyId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}`;
        return BaseService.delete(endpoint);
    }

    static async getPropertyAccessLogs(orgId, propertyId, lastAccessTime) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/access-logs${
            lastAccessTime ? `?lastAccessTime=${encodeURIComponent(lastAccessTime)}` : ''
        }`;
        return BaseService.get(endpoint);
    }

    static async checkInviteExists(orgId, propertyId, inviteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/check-invite`;
        return BaseService.post(endpoint, inviteData);
    }

    static async createUserInvite(orgId, propertyId, inviteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/invite`;
        return BaseService.post(endpoint, inviteData);
    }

    static async approveRegistration(orgId, propertyId, registrationId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/registrations/${registrationId}/approve`;
        return BaseService.post(endpoint, null);
    }

    static async approveRegistrations(orgId, propertyId, registrationIds) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/registrations/approve`;
        return BaseService.post(endpoint, { ids: registrationIds });
    }

    static async updateInvite(orgId, propertyId, inviteId, inviteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/invites/${inviteId}`;
        return BaseService.patch(endpoint, inviteData);
    }

    static async deleteInvite(orgId, propertyId, inviteId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/invites/${inviteId}`;
        return BaseService.delete(endpoint);
    }

    static async deleteInvites(orgId, propertyId, inviteIds) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/invites`;
        return BaseService.patch(endpoint, { ids: inviteIds });
    }

    static async addExistingUser(orgId, propertyId, userId, inviteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/users/${userId}`;
        return BaseService.post(endpoint, inviteData);
    }

    static async updateUser(orgId, propertyId, userId, userData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/users/${userId}`;
        return BaseService.patch(endpoint, userData);
    }

    static async removeUserAccess(orgId, propertyId, userId) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/users/${userId}`;
        return BaseService.delete(endpoint);
    }

    static async transferUser(orgId, propertyId, userId, transferData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/users/${userId}/transfer`;
        return BaseService.post(endpoint, transferData);
    }

    static async setRentalProperty(orgId, propertyId, rentalData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/set-rental`;
        return BaseService.post(endpoint, rentalData);
    }

    static async resetRentalProperty(orgId, propertyId, rentalData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/reset-rental`;
        return BaseService.post(endpoint, rentalData);
    }

    static async convertInvite(orgId, propertyId, inviteId, inviteData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/invites/${inviteId}/convert-invite`;
        return BaseService.post(endpoint, inviteData);
    }

    static async convertUser(orgId, propertyId, userId, userData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/users/${userId}/convert-user`;
        return BaseService.post(endpoint, userData);
    }

    static async changeEmailAddress(orgId, propertyId, userId, emailData) {
        const endpoint = `${this.ENDPOINT}/${orgId}/properties/${propertyId}/users/${userId}/change-email`;
        return BaseService.post(endpoint, emailData);
    }
}
